import { useEffect, useState } from 'react';
import {
  Show,
  useRecordContext,
  useNotify,
  useCreatePath,
  RaRecord,
} from 'react-admin';
import { useCompanies, companyNameByViaId } from '../shared/companies';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ClassificationChip } from './EmailList';
import {
  classificationLabel,
  preOrderStatusColor,
  preOrderStatusLabel,
  linkRoleLabel,
  linkRoleColor,
} from '../shared/status';
import { Row, SectionCard, fmtDate } from '../shared/Panel';
import { fileSizeText } from '../shared/number';
import { EmailPreview } from './EmailPreview';

const API = process.env.REACT_APP_NEST_API_URL || 'http://localhost:3001';
/** Nazwa spółki z danych — numery spółek nie mogą siedzieć w kodzie. */
const Company = ({ viaCompanyId, fallback }: { viaCompanyId?: string | null; fallback?: string | null }) => {
  const companies = useCompanies();
  return <span>{companyNameByViaId(companies, viaCompanyId) || fallback || '—'}</span>;
};
// —— prezlecenia z tego maila (1 Email → N PreOrder) ——
// Długowątkowy mail / lista ładunków może dać kilka prezleceń; każde ma własny
// panel z polami AI i statusem. Tu tylko lista z linkiem w dół.
const PreOrdersSection = ({ record }: { record: RaRecord }) => {
  const createPath = useCreatePath();
  const preOrders = (record.preOrders as RaRecord[] | undefined) ?? [];
  return (
    <SectionCard title={`Prezlecenia z tego maila (${preOrders.length})`}>
      {preOrders.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak prezleceń — z tej wiadomości nic jeszcze nie wyciągnięto.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {preOrders.map((p) => {
            const missing = (p.missingFields as string[] | undefined) ?? [];
            const orders = (p.orders as RaRecord[] | undefined) ?? [];
            return (
              <Box
                key={p.id as number}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderTop: '1px solid #eee', pt: 1 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 160 }}>
                  {(p.relation as string) || (p.clientName as string) || `Prezlecenie #${p.id as number}`}
                </Typography>
                <Chip size="small" color={preOrderStatusColor(p.status as string)} label={preOrderStatusLabel(p.status as string)} />
                <Chip
                  size="small"
                  variant="outlined"
                  color={missing.length ? 'warning' : 'success'}
                  label={missing.length ? `braki: ${missing.length}` : 'komplet'}
                />
                {orders.length > 0 && <Chip size="small" variant="outlined" label={`zlecenia: ${orders.length}`} />}
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  component={Link}
                  to={createPath({ resource: 'pre-orders', type: 'show', id: p.id as number })}
                >
                  Otwórz
                </Button>
              </Box>
            );
          })}
        </Stack>
      )}
    </SectionCard>
  );
};

// —— prezlecenia POWIĄZANE (nie utworzone z tego maila) ——
// Mail-zmiana / duplikat dotyczy istniejącego prezlecenia z innej wiadomości (N:M).
const LinkedPreOrdersSection = ({ record }: { record: RaRecord }) => {
  const createPath = useCreatePath();
  const links = ((record.preOrderLinks as RaRecord[] | undefined) ?? []).filter(
    (l) => (l.role as string) !== 'SOURCE',
  );
  if (links.length === 0) return null;
  return (
    <SectionCard title="Dotyczy istniejących prezleceń">
      <Stack spacing={1}>
        {links.map((l) => {
          const p = l.preOrder as RaRecord | undefined;
          return (
            <Box key={l.preOrderId as number} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip size="small" color={linkRoleColor(l.role as string)} label={linkRoleLabel(l.role as string)} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {(p?.relation as string) || (p?.clientName as string) || `Prezlecenie #${l.preOrderId as number}`}
              </Typography>
              <Button size="small" startIcon={<OpenInNewIcon />} component={Link} to={createPath({ resource: 'pre-orders', type: 'show', id: l.preOrderId as number })}>
                Otwórz
              </Button>
            </Box>
          );
        })}
      </Stack>
    </SectionCard>
  );
};

// —— historia wiadomości (wątek) ——
const ThreadSection = ({ id }: { id: number }) => {
  const [items, setItems] = useState<RaRecord[] | null>(null);
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API}/emails/${id}/thread`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setItems(d as RaRecord[]))
      .catch(() => setItems([]));
  }, [id]);
  if (items && items.length <= 1) return null;
  return (
    <SectionCard title="Historia wiadomości (wątek)">
      {items == null ? (
        <Typography variant="body2" color="text.secondary">Ładowanie…</Typography>
      ) : (
        <Stack spacing={1}>
          {items.map((m) => (
            <Box key={m.id as number} sx={{ opacity: (m.id as number) === id ? 1 : 0.7 }}>
              <Typography variant="body2" sx={{ fontWeight: (m.id as number) === id ? 600 : 400 }}>
                {(m.subject as string) || '(brak tematu)'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {m.fromName as string} · {fmtDate(m.receivedAt as string)}
                {m.classification ? ` · ${classificationLabel(m.classification as string)}` : ''}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
};

const EmailShowLayout = () => {
  const record = useRecordContext();
  const notify = useNotify();
  if (!record) return null;

  const attachments = (record.attachments as RaRecord[] | undefined) ?? [];
  const toList = (record.toAddresses as string[] | undefined) ?? [];
  const ccList = (record.ccAddresses as string[] | undefined) ?? [];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: 2, p: 1, alignItems: 'start' }}>
      {/* LEWA: podgląd */}
      <EmailPreview record={record} />

      {/* PRAWA: sekcje */}
      <Stack spacing={2}>
        <SectionCard title="Wiadomość">
          <Table size="small">
            <TableBody>
              <Row label="Od">{record.fromName as string} &lt;{record.fromAddress as string}&gt;</Row>
              <Row label="Do">{toList.join(', ') || '—'}</Row>
              {ccList.length ? <Row label="DW">{ccList.join(', ')}</Row> : <></>}
              <Row label="Temat">{(record.subject as string) || '—'}</Row>
              <Row label="Skrzynka">{(record.sourceMailbox as string) || '—'}</Row>
              <Row label="Otrzymano">{fmtDate(record.receivedAt as string)}</Row>
              <Row label="Klasyfikacja"><ClassificationChip record={record} /></Row>
              <Row label="Spółka">
                <Company
                  viaCompanyId={record.viaCompanyId as string | undefined}
                  fallback={(record.company as RaRecord | undefined)?.name as string | undefined}
                />
              </Row>
            </TableBody>
          </Table>
        </SectionCard>

        <PreOrdersSection record={record} />

        <LinkedPreOrdersSection record={record} />

        <ThreadSection id={record.id as number} />

        <SectionCard title={`Załączniki (${attachments.length})`}>
          {attachments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">brak</Typography>
          ) : (
            <>
              <Table size="small">
                <TableBody>
                  {attachments.map((a) => (
                    <TableRow key={a.id as number}>
                      <TableCell sx={{ border: 0 }}>{a.filename as string}</TableCell>
                      <TableCell sx={{ border: 0, color: 'text.secondary' }}>{a.contentType as string}</TableCell>
                      <TableCell sx={{ border: 0 }} align="right">{fileSizeText(a.size as number)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary">Pliki w S3: {attachments[0]?.s3Bucket as string}</Typography>
            </>
          )}
        </SectionCard>

        <SectionCard title="Akcje operatora (makieta)">
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} useFlexGap>
            <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />}
              onClick={() => notify('Klasyfikacja potwierdzona (makieta)', { type: 'success' })}>
              Potwierdź klasyfikację
            </Button>
            <Button size="small" variant="outlined" startIcon={<EditIcon />}
              onClick={() => notify('Popraw dane prezlecenia (makieta)', { type: 'info' })}>
              Popraw dane
            </Button>
            <Button size="small" variant="outlined" startIcon={<PlaylistAddIcon />}
              onClick={() => notify('Utworzono / zaktualizowano prezlecenie (makieta)', { type: 'info' })}>
              Utwórz prezlecenie
            </Button>
          </Stack>
        </SectionCard>
      </Stack>
    </Box>
  );
};

export const EmailShow = () => (
  <Show>
    <EmailShowLayout />
  </Show>
);
